import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useGameStore } from "../store/gameStore";
import { api, fileUrl } from "../lib/api";
import { openMapPopout, releasePopoutSlot } from "../lib/popout";
import { playSfxAsset, playSfxByTag, playNotificationChime } from "../lib/soundBus";
import { toast } from "sonner";
import MapCanvas from "../components/game/MapCanvas";
import GmSidebar from "../components/game/GmSidebar";
import BottomNav from "../components/game/BottomNav";
import ChatPanel from "../components/game/ChatPanel";
import WhispersPanel from "../components/game/WhispersPanel";
import NotesPanel from "../components/game/NotesPanel";
import CharacterSheetFull from "../components/game/CharacterSheetFull";
import CharacterCreatorModal from "../components/game/CharacterCreatorModal";
import PaperRoller from "../components/game/PaperRoller";
import HomebrewCharacterSheet from "../components/game/HomebrewCharacterSheet";
import DndCharacterSheet from "../components/game/DndCharacterSheet";
import FreeRoamHud from "../components/game/FreeRoamHud";
import DiceAnimationOverlay from "../components/game/DiceAnimationOverlay";
import GenericDiceRoller from "../components/game/GenericDiceRoller";
import TokenUploadCropModal from "../components/game/TokenUploadCropModal";
import TokenInspector from "../components/game/TokenInspector";
import MyTokensPanel from "../components/game/MyTokensPanel";
import CombatBanner from "../components/game/CombatBanner";
import GameLockBanner from "../components/game/GameLockBanner";
import { TrialCountdownBanner } from "../components/game/TrialCountdownBanner";
import WeatherOverlay from "../components/game/WeatherOverlay";
import AmbientMusicPlayer from "../components/game/AmbientMusicPlayer";
import VoicePanel from "../components/game/VoicePanel";
import ObstacleTriggerModal from "../components/game/ObstacleTriggerModal";
import TemplateTriggerModal from "../components/game/TemplateTriggerModal";
import SightConeTriggerModal from "../components/game/SightConeTriggerModal";
import RangeApprovalBar from "../components/game/RangeApprovalBar";
import TalkToGmBanner from "../components/game/TalkToGmBanner";
import CombatLogPanel from "../components/game/CombatLogPanel";
import NpcAttackModal from "../components/game/NpcAttackModal";
import ObstacleModifierBar from "../components/game/ObstacleModifierBar";
import SceneTabBar from "../components/game/SceneTabBar";
import DoorPromptBar from "../components/game/DoorPromptBar";
import DoorTriggerCheckModal from "../components/game/DoorTriggerCheckModal";
import PortalTransitionOverlay from "../components/game/PortalTransitionOverlay";
import GameShowPortalPrompt from "../components/game/GameShowPortalPrompt";
import SessionLogPanel from "../components/game/SessionLogPanel";
import CheckpointsPanel from "../components/game/CheckpointsPanel";
import CombatHUD from "../components/game/CombatHUD";
import PlayerHud from "../components/game/PlayerHud";
import { Button } from "../components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { NotebookText, Users, ArrowLeft, Video, History, X, Shield, ExternalLink, Flag, Lock } from "lucide-react";

const SIDE_TABS = [
  { id: "chat", label: "Chat" },
  { id: "whispers", label: "Whispers" },
  { id: "sheet", label: "Sheet" },
  { id: "dice", label: "Dice" },
  { id: "voice", label: "Voice" },
];

export default function GameRoomPage() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const isPopout = searchParams.get("popout") === "1";
  const popoutSceneId = searchParams.get("scene");
  const appliedPopoutSceneRef = useRef(false);
  const { game, tokens, templates, obstacles, walls, doors, online, combat, free_roam, activeSceneId, combatAlertPing, setGame, setActiveScene, setMyMemberId, upsertScene, fetchTokens, fetchTemplates, fetchObstacles, fetchWalls, fetchDoors, connectWebSocket, disconnectWebSocket, sendWs, setCombatAlertPing } = useGameStore();
  const [mobileTab, setMobileTab] = useState("map");
  const [desktopTab, setDesktopTab] = useState("chat");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(window.innerWidth < 768);
  const [notesOpen, setNotesOpen] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [sessionLogOpen, setSessionLogOpen] = useState(false);
  const [checkpointsOpen, setCheckpointsOpen] = useState(false);
  const [character, setCharacter] = useState(null);
  const [mySpeedFt, setMySpeedFt] = useState(null);
  const [myStats, setMyStats] = useState(null);
  const [creatorOpen, setCreatorOpen] = useState(false);
  const [tokenModalOpen, setTokenModalOpen] = useState(false);
  const [promptTokenPlacement, setPromptTokenPlacement] = useState(0);
  const [loading, setLoading] = useState(true);
  const [lockError, setLockError] = useState(null);
  const [billing, setBilling] = useState(null);
  const [renewing, setRenewing] = useState(false);
  const [voiceStatus, setVoiceStatus] = useState(null);
  const [voiceConnection, setVoiceConnection] = useState(null);
  const [voiceJoining, setVoiceJoining] = useState(false);
  const initialWhisperWith = searchParams.get("with");

  const memberIdToUserId = Object.fromEntries((game?.members || []).map((m) => [m.id, m.user_id]));

  const myMember = game?.members?.find((m) => m.user_id === user?.id);
  const isGm = myMember?.role === "gm";
  // Players view whichever scene their own token is actually on (so stepping through a door
  // genuinely moves just them); the GM's tab bar can look at any scene independently.
  const activeScene = game?.scenes?.find((s) => s.id === activeSceneId) || game?.scenes?.find((s) => s.id === game?.current_scene_id);
  const hasOwnPcToken = tokens.some((t) => t.created_by === myMember?.id && t.token_type === "pc");
  const myPcToken = tokens.find((t) => t.created_by === myMember?.id && t.token_type === "pc");
  const preCombatMusicRef = useRef(null);
  const lastTurnKeyRef = useRef(null);
  const [freeRoamCombatTrackUrl, setFreeRoamCombatTrackUrl] = useState(null);

  // Two deep "drum" hits (first hit, then a deeper second hit) whenever a brand-new Free Roam
  // fight starts, so the GM notices even if they're not looking at the Whispers tab right now.
  // Deep-linking straight into a specific whisper thread — e.g. from the Dashboard's whisper
  // inbox dropdown — jumps to the Whispers tab (both mobile and desktop) on load; the thread
  // itself is opened by WhispersPanel via `initialWhisperWith` below.
  useEffect(() => {
    if (searchParams.get("tab") === "whispers") {
      setDesktopTab("whispers");
      setMobileTab("whispers");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isGm && combatAlertPing && game?.id) playSfxByTag(game.id, "combat_alert");
  }, [isGm, combatAlertPing, game?.id]);

  // Free Roam can run several simultaneous encounters — only players whose member_id is actually
  // in an ACTIVE encounter's initiative order hear battle music, without hijacking the shared
  // scene track for everyone else still exploring peacefully (see AmbientMusicPlayer).
  const myFreeRoamEncounter = (free_roam?.encounters || []).find(
    (e) => e.active && (e.order || []).some((o) => o.member_id === myMember?.id)
  );
  useEffect(() => {
    if (!myFreeRoamEncounter || !gameId) return;
    api.get("/uploads", { params: { game_id: gameId, kind: "music", tag: "combat" } }).then((r) => {
      const track = r.data.find((a) => a.is_default) || r.data[0];
      setFreeRoamCombatTrackUrl(track ? fileUrl(track.id) : null);
    }).catch(() => {});
  }, [!!myFreeRoamEncounter, gameId]);

  const loadGame = useCallback(async () => {
    try {
      const { data } = await api.get(`/games/${gameId}`);
      setGame(data);
      setLoading(false);
      return data;
    } catch (err) {
      if (err.response?.status === 402) {
        const detail = err.response.data?.detail;
        const isOwner = typeof detail === "object" && detail !== null && !!detail.is_owner;
        setLockError({ message: (typeof detail === "object" ? detail.message : detail) || "This campaign is locked.", isOwner });
        setLoading(false);
        return null;
      }
      throw err;
    }
  }, [gameId, setGame]);

  useEffect(() => {
    if (isGm && game?.created_via_trial) {
      api.get("/billing/me").then((r) => setBilling(r.data)).catch(() => {});
    }
  }, [isGm, game?.created_via_trial]);

  useEffect(() => {
    loadGame();
    connectWebSocket(gameId);
    return () => disconnectWebSocket();
  }, [gameId]);

  // A popped-out map window jumps straight to whichever scene it was opened for, once — after
  // that the user is free to switch tabs (GM) same as any other view.
  useEffect(() => {
    if (isPopout && popoutSceneId && !appliedPopoutSceneRef.current && (game?.scenes || []).some((s) => s.id === popoutSceneId)) {
      appliedPopoutSceneRef.current = true;
      setActiveScene(popoutSceneId);
    }
  }, [isPopout, popoutSceneId, game?.scenes]);

  // Keep the shared 50-window pop-out budget honest even if this window is just closed via the OS.
  useEffect(() => {
    if (!isPopout) return;
    window.addEventListener("beforeunload", releasePopoutSlot);
    return () => window.removeEventListener("beforeunload", releasePopoutSlot);
  }, [isPopout]);

  const handlePopout = () => {
    if (!openMapPopout(gameId, activeSceneId)) {
      toast.error("Too many pop-out windows are already open (limit 50) — close a few and try again.");
    }
  };

  useEffect(() => {
    if (myMember?.id) setMyMemberId(myMember.id);
  }, [myMember?.id]);

  useEffect(() => {
    if (myMember?.id && !isGm) {
      api.get(`/games/${gameId}/tokens/mine`).then((r) => {
        if (r.data?.scene_id) setActiveScene(r.data.scene_id);
      }).catch(() => {});
    }
  }, [myMember?.id, isGm]);

  useEffect(() => {
    if (game && activeScene) {
      fetchTokens(gameId, activeScene.id);
      fetchTemplates(gameId, activeScene.id);
      fetchWalls(gameId, activeScene.id);
      fetchDoors(gameId, activeScene.id);
      if (isGm) fetchObstacles(gameId, activeScene.id);
    }
  }, [activeScene?.id, isGm]);

  useEffect(() => {
    if (myMember?.character_id) {
      api.get(`/characters/${myMember.character_id}`).then((r) => setCharacter(r.data)).catch(() => setCharacter(null));
    }
  }, [myMember?.character_id]);

  useEffect(() => {
    if (!character?.id) { setMySpeedFt(null); setMyStats(null); return; }
    api.get(`/characters/${character.id}/stats`).then((r) => { setMyStats(r.data); setMySpeedFt(r.data.speed); }).catch(() => { setMyStats(null); setMySpeedFt(null); });
  }, [character?.id]);

  useEffect(() => {
    api.get("/livekit/status").then((r) => setVoiceStatus(r.data.configured)).catch(() => setVoiceStatus(false));
  }, []);

  const joinVoice = async () => {
    setVoiceJoining(true);
    try {
      const { data } = await api.post("/livekit/token", { game_id: gameId });
      setVoiceConnection(data);
    } catch {
      setVoiceStatus(false);
    } finally {
      setVoiceJoining(false);
    }
  };

  // GM-only: auto-switch to combat music on combat start, revert on end
  useEffect(() => {
    if (!isGm || !activeScene) return;
    const switchMusic = async () => {
      if (combat.active) {
        preCombatMusicRef.current = { url: activeScene.music_asset_url, mood: activeScene.mood };
        const { data: assets } = await api.get("/uploads", { params: { game_id: gameId, kind: "music" } });
        const combatTrack = assets.find((a) => a.tag === "combat");
        if (combatTrack) {
          const url = fileUrl(combatTrack.id);
          const { data } = await api.patch(`/games/${gameId}/scenes/${activeScene.id}`, { music_asset_url: url, mood: "combat" });
          upsertScene(data);
        }
      } else if (preCombatMusicRef.current) {
        const prev = preCombatMusicRef.current;
        preCombatMusicRef.current = null;
        const { data } = await api.patch(`/games/${gameId}/scenes/${activeScene.id}`, { music_asset_url: prev.url, mood: prev.mood });
        upsertScene(data);
      }
    };
    switchMusic();
  }, [combat.active, isGm]);

  // Play a monster/token's assigned sound when its turn starts in combat, and give the owning
  // player a subtle chime + flash the tab title if it's THEIR own PC's turn and they're not
  // currently looking at the tab — small QoL so nobody has to keep asking "is it my turn yet?"
  const [isMyTurn, setIsMyTurn] = useState(false);
  useEffect(() => {
    if (!combat.active || !combat.order?.length) { setIsMyTurn(false); return; }
    const current = combat.order[combat.turn_index];
    const token = tokens.find((t) => t.id === current?.token_id);
    const mine = !!(token?.token_type === "pc" && token?.created_by === myMember?.id);
    setIsMyTurn(mine);
    const turnKey = `${combat.round}-${combat.turn_index}`;
    if (lastTurnKeyRef.current === turnKey) return;
    lastTurnKeyRef.current = turnKey;
    if (token?.sfx_asset_id) playSfxAsset(token.sfx_asset_id);
    if (mine) playNotificationChime();
  }, [combat.round, combat.turn_index, combat.active]);

  useEffect(() => {
    if (!isMyTurn) return;
    const original = document.title;
    let flashOn = false;
    const tick = () => {
      document.title = document.hidden ? (flashOn ? "Your Turn! — Call to Arms" : original) : original;
      flashOn = !flashOn;
    };
    tick();
    const interval = setInterval(tick, 1200);
    return () => { clearInterval(interval); document.title = original; };
  }, [isMyTurn]);

  const handleAddToken = () => setTokenModalOpen(true);

  const spawnToken = async (tokenData) => {
    if (!activeScene) return;
    await api.post(`/games/${gameId}/tokens`, {
      scene_id: activeScene.id,
      x: (activeScene.width || 1600) / 2,
      y: (activeScene.height || 1000) / 2,
      owner_member_id: myMember?.id,
      ...tokenData,
    });
  };

  const handleTokenUploaded = async ({ url, name }) => {
    if (!activeScene) return;
    await api.post(`/games/${gameId}/tokens`, {
      scene_id: activeScene.id,
      name,
      image_url: url,
      x: (activeScene.width || 1600) / 2,
      y: (activeScene.height || 1000) / 2,
      owner_member_id: myMember?.id,
      token_type: "npc",
    });
  };

  const renewAndReopen = async () => {
    setRenewing(true);
    try {
      const { data: b } = await api.get("/billing/me");
      const { data } = await api.post("/payments/checkout", { plan: b.last_plan || "monthly", origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout — try the Billing page instead.");
      setRenewing(false);
    }
  };

  if (lockError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1a1008] text-center p-8" data-testid="game-trial-locked-screen">
        <div className="max-w-md space-y-4">
          <Lock className="w-10 h-10 text-[#D4AF37]/50 mx-auto" />
          <p className="font-display text-2xl text-[#f1e0c5]">Campaign Locked</p>
          <p className="text-sm text-[#f1e0c5]/60">{lockError.message}</p>
          <div className="flex items-center justify-center gap-3 pt-2">
            {lockError.isOwner && (
              <Button data-testid="game-trial-locked-renew-button" onClick={renewAndReopen} disabled={renewing} className="rounded-full bg-[#D90429] hover:bg-[#b8031f]">
                {renewing ? "Opening Checkout…" : "Renew & Reopen"}
              </Button>
            )}
            <Button data-testid="game-trial-locked-dashboard-button" variant="secondary" onClick={() => navigate("/dashboard")} className="rounded-full">
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (loading || !game) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1a1008] text-[#f1e0c5]/50 text-sm uppercase tracking-widest">
        Loading room…
      </div>
    );
  }

  const renderSidePanel = (tab) => {
    if (tab === "chat") return <ChatPanel gameId={gameId} isGm={isGm} myMemberId={myMember?.id} members={game.members || []} />;
    if (tab === "whispers") return <WhispersPanel gameId={gameId} isGm={isGm} myMemberId={myMember?.id} members={game.members || []} initialOtherMemberId={initialWhisperWith} />;
    if (tab === "dice") return character ? <GenericDiceRoller gameId={gameId} characterId={character.id} isGm={isGm} members={game.members || []} myMemberId={myMember?.id} /> : <NoCharacterPrompt onCreate={() => setCreatorOpen(true)} />;
    if (tab === "sheet") {
      if (!character) return <NoCharacterPrompt onCreate={() => setCreatorOpen(true)} />;
      if (game.system === "homebrew") {
        return <HomebrewCharacterSheet gameId={gameId} character={character} onCharacterUpdate={setCharacter} isGm={isGm} members={game.members || []} myMemberId={myMember?.id} ruleset={game.homebrew_ruleset} />;
      }
      if (game.system === "dnd5e") {
        return <DndCharacterSheet gameId={gameId} character={character} onCharacterUpdate={setCharacter} isGm={isGm} members={game.members || []} myMemberId={myMember?.id} />;
      }
      return character.mode === "full" ? (
        <CharacterSheetFull gameId={gameId} character={character} onCharacterUpdate={setCharacter} isGm={isGm} members={game.members || []} myMemberId={myMember?.id} />
      ) : (
        <PaperRoller gameId={gameId} character={character} onCharacterUpdate={setCharacter} isGm={isGm} members={game.members || []} myMemberId={myMember?.id} />
      );
    }
    return null;
  };

  const voicePanelVisible = voiceOpen || desktopTab === "voice";

  return (
    <div className="relative w-screen h-screen overflow-hidden bg-[#1a1008] text-[#f1e0c5]">
      <MapCanvas
        game={game}
        scene={activeScene}
        tokens={tokens}
        templates={templates}
        obstacles={obstacles}
        walls={walls}
        doors={doors}
        myMemberId={myMember?.id}
        isGm={isGm}
        mySpeedFt={mySpeedFt}
        memberIdToUserId={memberIdToUserId}
        character={character}
        stats={myStats}
        onCharacterUpdate={setCharacter}
      />
      {isGm && <SceneTabBar gameId={gameId} game={game} tokens={tokens} sidebarCollapsed={sidebarCollapsed} />}
      <WeatherOverlay weather={activeScene?.weather} />
      <AmbientMusicPlayer scene={activeScene} masterVolume={game.music_volume ?? 0.6} combatOverrideUrl={myFreeRoamEncounter ? freeRoamCombatTrackUrl : null} />

      {isGm && (
        <GmSidebar
          game={game}
          scene={activeScene}
          members={game.members || []}
          tokens={tokens}
          onGameUpdate={setGame}
          onSceneUpdate={upsertScene}
          onAddToken={handleAddToken}
          onSpawnToken={spawnToken}
          collapsed={sidebarCollapsed}
          setCollapsed={setSidebarCollapsed}
        />
      )}

      <CombatBanner game={game} combat={combat} isGm={isGm} />
      <GameLockBanner game={game} isGm={isGm} freeRoamEnabled={free_roam?.enabled} />
      {isGm && game.created_via_trial && <TrialCountdownBanner billing={billing} variant="pill" />}
      <FreeRoamHud gameId={gameId} character={character} myMember={myMember} isGm={isGm} sidebarCollapsed={sidebarCollapsed} isRaid={!!game.raid_id} />
      <DiceAnimationOverlay />
      <ObstacleTriggerModal gameId={gameId} character={character} myMemberId={myMember?.id} myMemberName={myMember?.display_name} isGm={isGm} />
      <TemplateTriggerModal gameId={gameId} character={character} myMemberId={myMember?.id} myMemberName={myMember?.display_name} />
      <SightConeTriggerModal gameId={gameId} character={character} myMemberId={myMember?.id} myMemberName={myMember?.display_name} />
      <TalkToGmBanner isGm={isGm} gameId={game.id} onOpenWhispers={() => { setMobileTab("whispers"); setDesktopTab("whispers"); }} />
      <NpcAttackModal gameId={gameId} myMemberId={myMember?.id} isGm={isGm} />
      <RangeApprovalBar isGm={isGm} sendWs={sendWs} />
      <ObstacleModifierBar isGm={isGm} sendWs={sendWs} />
      <DoorPromptBar gameId={gameId} isGm={isGm} />
      <DoorTriggerCheckModal gameId={gameId} character={character} myMemberId={myMember?.id} isGm={isGm} />
      <PortalTransitionOverlay myMemberId={myMember?.id} />
      <GameShowPortalPrompt myMemberId={myMember?.id} />
      <CombatHUD gameId={gameId} character={character} stats={myStats} combat={combat} />
      <CombatLogPanel isGm={isGm} sidebarCollapsed={sidebarCollapsed} />

      <div className="absolute top-4 right-4 z-20 flex items-center gap-2">
        {!isPopout && (
          <button data-testid="back-to-dashboard-button" onClick={() => navigate("/dashboard")} className="w-10 h-10 rounded-full bg-black/60 backdrop-blur-2xl border border-white/10 flex items-center justify-center hover:border-[#D4AF37] transition-colors duration-200">
            <ArrowLeft className="w-4 h-4" />
          </button>
        )}
        {!isPopout && isGm && game.raid_id && (
          <button data-testid="back-to-raid-lobby-button" title="Raid Lobby" onClick={() => navigate(`/raid/${game.raid_id}`)} className="px-3 h-10 rounded-full bg-[#1E3A8A]/70 backdrop-blur-2xl border border-[#3B82F6]/40 flex items-center gap-1.5 text-xs hover:bg-[#1E3A8A] transition-colors duration-200">
            <Shield className="w-3.5 h-3.5" /> Raid Lobby
          </button>
        )}
        <div className="px-3 h-10 rounded-full bg-black/60 backdrop-blur-2xl border border-white/10 flex items-center gap-2 text-xs" data-testid="online-count-badge">
          <Users className="w-3.5 h-3.5 text-[#D4AF37]" /> {online}
        </div>
        {!isPopout && (
          <button data-testid="popout-map-button" title="Pop this map out into its own window" onClick={handlePopout} className="hidden md:flex w-10 h-10 rounded-full bg-black/60 backdrop-blur-2xl border border-white/10 items-center justify-center hover:border-[#D4AF37] transition-colors duration-200">
            <ExternalLink className="w-4 h-4" />
          </button>
        )}
        {!isPopout && (
          <button data-testid="open-voice-button" onClick={() => setVoiceOpen(true)} className="md:hidden w-10 h-10 rounded-full bg-black/60 backdrop-blur-2xl border border-white/10 flex items-center justify-center hover:border-[#D4AF37] transition-colors duration-200">
            <Video className="w-4 h-4" />
          </button>
        )}
        {!isPopout && (
          <button data-testid="open-notes-button" onClick={() => setNotesOpen(true)} className="w-10 h-10 rounded-full bg-black/60 backdrop-blur-2xl border border-white/10 flex items-center justify-center hover:border-[#D4AF37] transition-colors duration-200">
            <NotebookText className="w-4 h-4" />
          </button>
        )}
        {!isPopout && (
          <button data-testid="open-session-log-button" onClick={() => setSessionLogOpen(true)} className="w-10 h-10 rounded-full bg-black/60 backdrop-blur-2xl border border-white/10 flex items-center justify-center hover:border-[#D4AF37] transition-colors duration-200">
            <History className="w-4 h-4" />
          </button>
        )}
        {!isPopout && (
          <button data-testid="open-checkpoints-button" title="Checkpoints" onClick={() => setCheckpointsOpen(true)} className="w-10 h-10 rounded-full bg-black/60 backdrop-blur-2xl border border-white/10 flex items-center justify-center hover:border-[#D4AF37] transition-colors duration-200">
            <Flag className="w-4 h-4" />
          </button>
        )}
      </div>

      <MyTokensPanel
        gameId={gameId}
        scene={activeScene}
        myMember={myMember}
        hasOwnPcToken={hasOwnPcToken}
        character={character}
        onNeedCharacter={() => setCreatorOpen(true)}
        autoOpenTrigger={promptTokenPlacement}
      />
      {!isGm && <PlayerHud token={myPcToken} character={character} stats={myStats} />}

      {/* Desktop docked side panel — hidden in a pop-out window, which is map-focused only */}
      {!isPopout && (
        <div className="hidden md:flex absolute top-4 right-4 bottom-4 w-[380px] z-20 flex-col bg-black/60 backdrop-blur-2xl border border-white/10 rounded-2xl overflow-hidden mt-14">
          <div className="flex border-b border-white/10">
            {SIDE_TABS.map((t) => {
              const alerting = isGm && combatAlertPing && t.id === "whispers";
              return (
                <button
                  key={t.id}
                  data-testid={`desktop-tab-${t.id}`}
                  onClick={() => { setDesktopTab(t.id); if (t.id === "whispers") setCombatAlertPing(false); }}
                  className={`relative flex-1 py-3 text-xs uppercase tracking-widest transition-colors duration-200 ${desktopTab === t.id ? "text-[#D4AF37] border-b-2 border-[#D4AF37]" : alerting ? "text-[#D90429] animate-pulse" : "text-[#f1e0c5]/40"}`}
                >
                  {t.label}
                  {alerting && <span data-testid="desktop-tab-alert-dot" className="absolute top-1.5 right-3 w-1.5 h-1.5 rounded-full bg-[#D90429] shadow-[0_0_6px_#D90429]" />}
                </button>
              );
            })}
          </div>
          <div className="flex-1 overflow-hidden relative">
            {SIDE_TABS.filter((t) => t.id !== "voice").map((t) => (
              <div key={t.id} className={`h-full ${desktopTab === t.id ? "block" : "hidden"}`}>
                {renderSidePanel(t.id)}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Single always-mounted voice panel (repositioned responsively) so a live LiveKit
          connection never drops just because the user switched tabs or closed the sheet.
          On desktop it's offset to start BELOW the tab bar above (not at md:top-4, where the
          docked panel's tab bar lives) so Chat/Notes/etc. stay reachable without needing the
          map's exit arrow — it never visually covers the tabs used to switch away from Voice.
          Skipped entirely in a pop-out window to avoid a second, feedback-prone LiveKit connection. */}
      {!isPopout && (
        <div
          className={`fixed z-40 flex-col bg-[#322216] inset-0 pb-16 ${voiceOpen ? "flex" : "hidden"}
          md:inset-auto md:top-[115px] md:right-4 md:bottom-4 md:w-[380px] md:pb-0 md:rounded-b-2xl md:border md:border-white/10 md:bg-black/60 md:backdrop-blur-2xl md:overflow-hidden
          ${desktopTab === "voice" ? "md:flex" : "md:hidden"}`}
        >
          <div className="p-4 border-b border-white/10 flex items-center justify-between shrink-0 md:hidden">
            <h2 className="font-display text-2xl text-[#f1e0c5]">Voice &amp; Video</h2>
            <button data-testid="close-voice-panel-button" onClick={() => setVoiceOpen(false)} className="w-8 h-8 rounded-full flex items-center justify-center text-[#f1e0c5]/50 hover:text-[#D4AF37]">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex-1 overflow-hidden">
            <VoicePanel gameId={gameId} status={voiceStatus} connection={voiceConnection} joining={voiceJoining} onJoin={joinVoice} onDisconnected={() => { setVoiceConnection(null); useGameStore.getState().setVoiceWhisper(null); }} isGm={isGm} myUserId={user?.id} myMemberId={myMember?.id} members={game.members || []} />
          </div>
        </div>
      )}

      {/* Mobile full-screen panel */}
      {!isPopout && mobileTab !== "map" && (
        <div className="md:hidden absolute inset-0 z-20 bg-[#1a1008] pb-16 pt-4">{renderSidePanel(mobileTab)}</div>
      )}

      <TokenInspector
        gameId={gameId}
        isGm={isGm}
        myMemberId={myMember?.id}
        members={game.members || []}
        game={game}
        scene={activeScene}
        character={character}
        stats={myStats}
        onOpenSheet={() => { setMobileTab("sheet"); setDesktopTab("sheet"); }}
      />
      {!isPopout && (
        <BottomNav
          active={mobileTab}
          onChange={(tab) => { setMobileTab(tab); if (tab === "whispers") setCombatAlertPing(false); }}
          alertTabId={isGm && combatAlertPing ? "whispers" : null}
        />
      )}

      <Sheet open={notesOpen} onOpenChange={setNotesOpen}>
        <SheetContent side="right" className="bg-[#322216] border-white/10 text-[#f1e0c5] w-full sm:max-w-md p-0">
          <SheetHeader className="p-4 border-b border-white/10">
            <SheetTitle className="font-display text-2xl text-[#f1e0c5]">Notes</SheetTitle>
          </SheetHeader>
          <NotesPanel gameId={gameId} isGm={isGm} />
        </SheetContent>
      </Sheet>

      <Sheet open={sessionLogOpen} onOpenChange={setSessionLogOpen}>
        <SheetContent side="right" className="bg-[#322216] border-white/10 text-[#f1e0c5] w-full sm:max-w-md p-0">
          <SheetHeader className="p-4 border-b border-white/10">
            <SheetTitle className="font-display text-2xl text-[#f1e0c5]">Session Log</SheetTitle>
          </SheetHeader>
          <SessionLogPanel gameId={gameId} isGm={isGm} />
        </SheetContent>
      </Sheet>

      <Sheet open={checkpointsOpen} onOpenChange={setCheckpointsOpen}>
        <SheetContent side="right" className="bg-[#322216] border-white/10 text-[#f1e0c5] w-full sm:max-w-md p-0">
          <SheetHeader className="p-4 border-b border-white/10">
            <SheetTitle className="font-display text-2xl text-[#f1e0c5]">Checkpoints</SheetTitle>
          </SheetHeader>
          <CheckpointsPanel gameId={gameId} isGm={isGm} />
        </SheetContent>
      </Sheet>

      <CharacterCreatorModal
        open={creatorOpen}
        onOpenChange={setCreatorOpen}
        gameId={gameId}
        game={game}
        onCreated={(c) => { setCharacter(c); loadGame(); if (!hasOwnPcToken) setPromptTokenPlacement((n) => n + 1); }}
      />
      <TokenUploadCropModal open={tokenModalOpen} onOpenChange={setTokenModalOpen} gameId={gameId} onUploaded={handleTokenUploaded} />
    </div>
  );
}

function NoCharacterPrompt({ onCreate }) {
  return (
    <div className="h-full flex flex-col items-center justify-center gap-4 p-8 text-center">
      <p className="text-[#f1e0c5]/50 text-sm">You haven't created a character for this table yet.</p>
      <Button data-testid="create-character-prompt-button" onClick={onCreate} className="rounded-full bg-[#D90429] hover:bg-[#b8031f] uppercase tracking-widest text-xs">
        Create Character
      </Button>
    </div>
  );
}
